<script setup lang="ts">
  import { type PropType, ref } from 'vue';
  import type { Photo } from '@storefront/resources';
  import { useBreakpoints } from '@storefront/storefront';

  defineOptions({
    inheritAttrs: false,
  });

  defineProps({
    header: { type: String, required: true },
    content: String,
    headerType: { type: String },
    photos: { type: Array as PropType<Photo[]>, required: true },
    products: { type: Array, required: true },
    link: String,
    photoLink: String,
    ariaLabel: String,
    enableAutoplay: Boolean,
    timeAutoplay: [Number, String],
    speed: [Number, String],
  });

  const breakpoints = useBreakpoints();
  const parameters = ref({
    breakpoints: {
      1: {
        slidesPerView: 1.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      576: {
        slidesPerView: 2.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      768: {
        slidesPerView: 3.5,
        spaceBetween: 10,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      992: {
        slidesPerView: 2.5,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      1400: {
        slidesPerView: 4,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
  });
</script>
<template>
  <section v-if="products?.length > 0" class="photo-product-slider-block" :class="{'photo-product-slider-block--no-photo' : !(photos?.length > 0)}">
    <div class="photo-product-slider-block__photo-wrapper" v-if="photos?.length > 0 && breakpoints.lg">
      <MediaPicture :photo="photos" type="photo" sizes="600x884 2x, 300x442" />
      <a v-if="photoLink" :href="photoLink" :aria-label="ariaLabel" class="photo-product-slider-block__link" :title="ariaLabel" />
    </div>
    <SfSection div :title="header" :headerType :link="link || $route('web.product.index')">
      <QuillContent v-if="content" :content />
      <SfProductsCarousel :parameters :products :enableAutoplay :timeAutoplay :speed>
        <template #before v-if="photos?.length > 0 && !breakpoints.lg">
          <div class="photo-product-slider-block__photo-wrapper">
            <MediaPicture :photo="photos" type="photo" sizes="600x900 2x, 300x450" />
            <a v-if="photoLink" :href="photoLink" :aria-label="ariaLabel " class="photo-product-slider-block__link" :title="ariaLabel" />
          </div>
        </template>
      </SfProductsCarousel>
    </SfSection>
  </section>
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .photo-product-slider-block {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 1rem;
    --section-gap: 1rem;
    --section-content-gap: 1rem;

    &--no-photo {
      grid-template-columns: 1fr;
    }

    &__photo-wrapper {
      position: relative;
      height: 100%;
      overflow: hidden;
      border-radius: var(--section-border-radius);

      :deep(img) {
        height: 100%;
        object-fit: cover;
        aspect-ratio: 2/3;
      }
    }

    &__link {
      position: absolute;
      inset: 0;
    }

    @include media.query(lg) {
      grid-template-columns: 1fr;

      &__photo-wrapper {
        border-radius: var(--border-radius);
        aspect-ratio: 2/3;
        width: auto
      }
    }
  }
</style>
